import { cloneDeep } from "lodash";

import { Layer } from "@/Utils/hierarchy";

import { ChannelTotalROIMap, SharedRevenueChannelTotalROIMap } from "../types";

const createBaseData = () => ({
  id: "",
  mappedName: "",
  brand: "",
  category: "",
  product: "",
  channel: "",
  geography: "",
  sampleSize: 0,
  adstockPeakTotal: 0,
  adstockDurationTotal: 0,
  unit: 0,
  adstockPeak: 0,
  adstockDuration: 0,
  units: {
    [Layer.Brand]: 0,
    [Layer.Category]: 0,
    [Layer.Product]: 0,
    total: 0,
    upperFunnelMarketingContribution: 0,
    midFunnelMarketingContribution: 0,
  },
  yield: {
    [Layer.Brand]: 0,
    [Layer.Category]: 0,
    [Layer.Product]: 0,
    total: 0,
  },
  spendPercentage: {
    [Layer.Brand]: 0,
    [Layer.Category]: 0,
    [Layer.Product]: 0,
    total: 0,
  },
  revenuePercentage: {
    [Layer.Brand]: 0,
    [Layer.Category]: 0,
    [Layer.Product]: 0,
    total: 0,
    upperFunnelMarketingContribution: 0,
    midFunnelMarketingContribution: 0,
  },
  roi: {
    [Layer.Brand]: 0,
    [Layer.Category]: 0,
    [Layer.Product]: 0,
    total: 0,
  },
  contribution: {
    [Layer.Brand]: 0,
    [Layer.Category]: 0,
    [Layer.Product]: 0,
    total: 0,
  },
  roas: {
    [Layer.Brand]: 0,
    [Layer.Category]: 0,
    [Layer.Product]: 0,
    total: 0,
  },
  revenue: {
    [Layer.Brand]: 0,
    [Layer.Category]: 0,
    [Layer.Product]: 0,
    total: 0,
    upperFunnelMarketingContribution: 0,
    midFunnelMarketingContribution: 0,
  },
  spend: {
    [Layer.Brand]: 0,
    [Layer.Category]: 0,
    [Layer.Product]: 0,
    total: 0,
  },
});

export const mergeSharedRevenueData = (
  data: ChannelTotalROIMap,
  sharedData: SharedRevenueChannelTotalROIMap,
  total: number
): ChannelTotalROIMap => {
  const mergedData = cloneDeep(data);

  // First handle all channels that exist in ROI data
  Object.entries(mergedData).forEach(([channel, channelData]) => {
    const sharedChannelData = sharedData[channel];
    if (sharedChannelData) {
      // Update existing channel data with shared data
      const sharedRevenue = sharedChannelData.revenue;
      const sharedRevenuePercentage = sharedChannelData.revenuePercentage;
      const totalRevenue =
        (channelData?.revenue?.[Layer.Brand] ?? 0) +
        (channelData?.revenue?.[Layer.Category] ?? 0) +
        (channelData?.revenue?.[Layer.Product] ?? 0) +
        (sharedChannelData.revenue?.brand ?? 0) +
        (sharedChannelData.revenue?.category ?? 0);

      if (sharedRevenue && channelData.revenue) {
        channelData.revenue.upperFunnelMarketingContribution = sharedRevenue.brand ?? 0;
        channelData.revenue.midFunnelMarketingContribution = sharedRevenue.category ?? 0;
        channelData.revenue.total = totalRevenue;
      }

      // Update units with shared data
      if (sharedChannelData.units && channelData.units) {
        channelData.units.upperFunnelMarketingContribution = sharedChannelData.units.brand ?? 0;
        channelData.units.midFunnelMarketingContribution = sharedChannelData.units.category ?? 0;
        channelData.units.total =
          (sharedChannelData.units.brand ?? 0) + (sharedChannelData.units.category ?? 0);
      }

      // Update sample size to take the larger value
      channelData.sampleSize = Math.max(
        channelData.sampleSize ?? 0,
        sharedChannelData.sampleSize ?? 0
      );

      if (sharedRevenuePercentage && channelData.revenuePercentage) {
        // Calculate percentages based on the new total
        const upperFunnelRevenue = sharedRevenue?.brand ?? 0;
        const midFunnelRevenue = sharedRevenue?.category ?? 0;

        channelData.revenuePercentage.upperFunnelMarketingContribution =
          total > 0 ? (upperFunnelRevenue / total) * 100 : 0;
        channelData.revenuePercentage.midFunnelMarketingContribution =
          total > 0 ? (midFunnelRevenue / total) * 100 : 0;
        channelData.revenuePercentage.total = total > 0 ? (totalRevenue / total) * 100 : 0;

        // Recalculate other percentages
        channelData.revenuePercentage[Layer.Brand] =
          total > 0 ? (channelData.revenue[Layer.Brand] / total) * 100 : 0;
        channelData.revenuePercentage[Layer.Category] =
          total > 0 ? (channelData.revenue[Layer.Category] / total) * 100 : 0;
        channelData.revenuePercentage[Layer.Product] =
          total > 0 ? (channelData.revenue[Layer.Product] / total) * 100 : 0;
      }
    } else {
      // Channel exists in ROI but not in shared data
      // Update percentages based on existing revenue
      if (channelData.revenuePercentage) {
        channelData.revenuePercentage.total =
          total > 0 ? (channelData.revenue.total / total) * 100 : 0;
        channelData.revenuePercentage[Layer.Brand] =
          total > 0 ? (channelData.revenue[Layer.Brand] / total) * 100 : 0;
        channelData.revenuePercentage[Layer.Category] =
          total > 0 ? (channelData.revenue[Layer.Category] / total) * 100 : 0;
        channelData.revenuePercentage[Layer.Product] =
          total > 0 ? (channelData.revenue[Layer.Product] / total) * 100 : 0;
      }
    }
  });

  // Then add any channels that only exist in shared data
  Object.entries(sharedData).forEach(([channel, sharedChannelData]) => {
    if (!mergedData[channel]) {
      const baseData = createBaseData();
      const upperFunnelRevenue = sharedChannelData.revenue?.brand ?? 0;
      const midFunnelRevenue = sharedChannelData.revenue?.category ?? 0;
      const channelTotalRevenue = upperFunnelRevenue + midFunnelRevenue;

      mergedData[channel] = {
        ...baseData,
        id: sharedChannelData.id,
        geography: sharedChannelData.geography,
        channel: sharedChannelData.channel,
        brand: (sharedChannelData as any).brand,
        category: (sharedChannelData as any).category,
        product: "Brand SOE",
        sampleSize: sharedChannelData.sampleSize ?? 0,
        revenue: {
          ...baseData.revenue,
          upperFunnelMarketingContribution: upperFunnelRevenue,
          midFunnelMarketingContribution: midFunnelRevenue,
          total: channelTotalRevenue,
        },
        revenuePercentage: {
          ...baseData.revenuePercentage,
          upperFunnelMarketingContribution: total > 0 ? (upperFunnelRevenue / total) * 100 : 0,
          midFunnelMarketingContribution: total > 0 ? (midFunnelRevenue / total) * 100 : 0,
          total: total > 0 ? (channelTotalRevenue / total) * 100 : 0,
        },
        units: {
          ...baseData.units,
          upperFunnelMarketingContribution: sharedChannelData.units?.brand ?? 0,
          midFunnelMarketingContribution: sharedChannelData.units?.category ?? 0,
          total: (sharedChannelData.units?.brand ?? 0) + (sharedChannelData.units?.category ?? 0),
        },
      };
    }
  });

  return mergedData;
};
